// Sprint 5 — Módulo de Clientes CRM

import { useEffect, useState } from "react";
import { css } from "styled-system/css";
import { Modal } from "@/components/ui/modal";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

export function CreateClientModal({
  open,
  onClose,
  onCreated,
  professionalQuery,
  lockEmail,
  initialEmail,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  professionalQuery: string;
  lockEmail?: boolean;
  initialEmail?: string;
}) {
  const { refresh } = useAuth();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setMsg(null);
    setFirstName("");
    setLastName("");
    setEmail(lockEmail && initialEmail ? initialEmail : "");
    setPhone("");
    setInternalNotes("");
  }, [open, lockEmail, initialEmail]);

  async function submit() {
    setBusy(true);
    setMsg(null);
    const body = {
      firstName: firstName.trim(),
      lastName: lastName.trim() || null,
      email: email.trim(),
      phone: phone.trim() || null,
      internalNotes: internalNotes.trim() || null,
    };
    const r = await fetchWithSessionSync(
      `/api/dashboard/clients${professionalQuery}`,
      { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
      refresh,
    );
    setBusy(false);
    if (r.status === 409) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setMsg(j.error ?? "Ya existe un cliente con este email. Buscá el contacto en la tabla.");
      return;
    }
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setMsg(j.error ?? "No se pudo crear el cliente");
      return;
    }
    onCreated();
    onClose();
  }

  return (
    <Modal open={open} onClose={onClose} title="Nuevo cliente">
      {msg ? <p className={css({ fontSize: "sm", color: "#b91c1c", mb: "3" })}>{msg}</p> : null}
      <div className={css({ display: "flex", flexDir: "column", gap: "4" })}>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Nombre</span>
          <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} autoComplete="given-name" />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Apellido</span>
          <Input value={lastName} onChange={(e) => setLastName(e.target.value)} autoComplete="family-name" />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Email (requerido)</span>
          <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={!!lockEmail} autoComplete="email" />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Teléfono</span>
          <Input value={phone} onChange={(e) => setPhone(e.target.value)} autoComplete="tel" />
        </label>
        <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
          <span className={css({ fontSize: "xs", color: "#64748b" })}>Notas internas</span>
          <textarea
            className={css({ padding: "10px 12px", borderRadius: "10px", border: "1px solid #e2e8f0", minHeight: "80px" })}
            value={internalNotes}
            onChange={(e) => setInternalNotes(e.target.value)}
          />
        </label>
        <div className={css({ display: "flex", gap: "2", justifyContent: "flex-end" })}>
          <Button variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" disabled={busy || !firstName.trim() || !email.trim()} onClick={() => void submit()}>
            {busy ? "Guardando…" : "Guardar"}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
